import { useEffect } from 'react';
import type { Question } from '@omq/types';
import { QuestionAnswerModal, AnswersComponent, useBackendContext } from '@omq/shared';

import { HelpAPI } from '../../api/help';

/**
 * Type for component properties.
 */
type HelpInfoProps = {
  question: Question | null;
  onClose: () => void;
};

/**
 * Component for OMQHelpInfo.
 *
 * Displays passed question in modal component.
 *
 * @param {HelpInfoProps} props - Component properties
 *
 * @author Florian Walch
 * @since 9.5
 *
 * @returns {JSX.Element}
 */
export function HelpInfo(props: HelpInfoProps): JSX.Element {
  const { question, onClose } = props;

  const backend = useBackendContext();

  // send tracking request
  useEffect(() => {
    if (question != null) {
      HelpAPI.trackView(backend, question.id);
    }
  }, [backend, question]);

  // close if there are no answers to show.
  // notifies embedding component that there
  // was nothing to see & component has been closed
  useEffect(() => {
    if (question == null || question.answers.length === 0) {
      if (onClose != null) {
        onClose();
      }
    }
  }, [question, onClose]);

  if (question == null) {
    return null;
  }

  const { answers } = question;

  // do nothing if there are no answers
  if (answers == null || answers.length === 0) {
    return null;
  }

  // render component
  return (
    <QuestionAnswerModal headline={question.body} className="help-info" onClose={onClose}>
      <AnswersComponent question={question} className="help-info" isModal={true} />
    </QuestionAnswerModal>
  );
}
